import type {
  CustomGraph,
  SheetColumn,
  SheetFilters,
  TriggerConfig,
} from '@/types/custom-playbooks';
import { columnKey } from './sheet-link.util';
export type {
  SheetFieldType,
  SheetDateFormat,
  SheetColumn,
  SheetFilters,
} from '@/types/custom-playbooks';

export const SHEET_IDENTITY_FIELDS = [
  ['email', 'Email'],
  ['linkedinUrl', 'LinkedIn URL'],
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['fullName', 'Full name'],
  ['title', 'Job title'],
  ['companyName', 'Company name'],
  ['companyDomain', 'Company domain'],
  ['externalId', 'External ID'],
] as const;
export interface SheetColumnsResponse {
  spreadsheetId: string;
  spreadsheetName: string;
  spreadsheetUrl?: string | null;
  sheetId: number;
  sheetName: string;
  googleUserId: string;
  accountLabel?: string | null;
  tabs: { sheetId: number; sheetName: string }[];
  columns: SheetColumn[];
  identity: SheetFilters['identity'];
  rowKeyCandidates: string[];
  rows: number;
}
export interface SheetDraft {
  source?: string;
  kind?: string;
  sheet?: Partial<SheetFilters>;
  rerunAfterDays?: number;
  skipTouchedWithinDays?: number;
}

export function sheetTriggerIssue(
  sheet?: Partial<SheetFilters>
): string | null {
  if (
    !sheet ||
    !/^[A-Za-z0-9_-]{10,200}$/.test(sheet.spreadsheetId ?? '') ||
    !Number.isInteger(sheet.sheetId) ||
    (sheet.sheetId ?? -1) < 0 ||
    typeof sheet.googleUserId !== 'string' ||
    !sheet.googleUserId.trim() ||
    typeof sheet.sheetName !== 'string' ||
    !sheet.sheetName.trim() ||
    sheet.sheetName.length > 200 ||
    sheet.headerRow !== 1 ||
    !['person', 'company'].includes(sheet.recordKind ?? '') ||
    !['now', 'historic'].includes(sheet.backfill ?? '') ||
    (sheet.scope != null && !['new_rows', 'all_rows'].includes(sheet.scope)) ||
    (sheet.rowEmailToCrm !== undefined &&
      typeof sheet.rowEmailToCrm !== 'boolean')
  )
    return 'Pick a sheet and tab.';
  if (!Array.isArray(sheet.columns))
    return 'Pick the sheet to read its columns.';
  const columns = sheet.columns;
  if (
    columns.some(
      (column) =>
        !column ||
        typeof column.key !== 'string' ||
        typeof column.header !== 'string'
    )
  ) {
    return 'Every mapping must use a saved sheet column.';
  }
  const keys = new Set(columns.map((column) => column.key));
  if (
    columns.length > 50 ||
    keys.size !== columns.length ||
    columns.some(
      (column) =>
        !/^[a-z][a-z0-9_]{0,39}$/.test(column.key) ||
        column.header.length > 200 ||
        !['string', 'number', 'date', 'boolean'].includes(column.type) ||
        (column.format != null &&
          (column.type !== 'date' ||
            !['YYYY-MM-DD', 'M/D/YYYY', 'D/M/YYYY'].includes(column.format)))
    ) ||
    [...Object.values(sheet.identity ?? {}), sheet.rowKey].some(
      (key) => key != null && !keys.has(key)
    )
  )
    return 'Every mapping must use a saved sheet column.';
  if (
    sheet.recordKind === 'company'
      ? !sheet.identity?.companyDomain
      : !sheet.identity?.email && !sheet.identity?.linkedinUrl
  )
    return sheet.recordKind === 'company'
      ? 'Map a company domain column.'
      : 'Map an email or LinkedIn URL column.';
  return null;
}

export function pickedSheetDraft<T extends SheetDraft>(
  draft: T,
  response: SheetColumnsResponse
): T & {
  source: 'google_sheet';
  kind: 'sheet_rows_added';
  sheet: SheetFilters;
} {
  const kept = keptSheetMapping(draft.sheet, response);
  const identity = kept?.identity ?? response.identity;
  const key = identity.externalId ?? identity.email;
  const next = {
    ...draft,
    source: 'google_sheet' as const,
    kind: 'sheet_rows_added' as const,
    sheet: {
      spreadsheetId: response.spreadsheetId,
      spreadsheetName: response.spreadsheetName,
      spreadsheetUrl: response.spreadsheetUrl,
      sheetId: response.sheetId,
      sheetName: response.sheetName,
      googleUserId: response.googleUserId,
      accountLabel: response.accountLabel,
      recordKind: draft.sheet?.recordKind ?? 'person',
      headerRow: 1 as const,
      columns: kept?.columns ?? response.columns,
      identity,
      rowKey:
        kept?.rowKey !== undefined
          ? kept.rowKey
          : key && response.rowKeyCandidates.includes(key)
            ? key
            : null,
      backfill: draft.sheet?.backfill ?? 'now',
      ...(draft.sheet?.scope === 'all_rows'
        ? { scope: 'all_rows' as const }
        : {}),
      ...(draft.sheet?.rowEmailToCrm === false ? { rowEmailToCrm: false } : {}),
      ...(draft.sheet?.perPeriod != null
        ? { perPeriod: draft.sheet.perPeriod }
        : {}),
    },
  };
  delete next.rerunAfterDays;
  delete next.skipTouchedWithinDays;
  return next;
}

/**
 * Re-reading the tab a trigger already saved (to refresh its columns) keeps
 * what the member chose. Columns match by trimmed, case-insensitive header
 * when the header is unique
 * in both the saved and the read columns: they keep their saved key, type
 * and format. Read columns with no match join with the detected type, and
 * saved columns no longer in the sheet are dropped. A mapping or row key
 * keeps its saved column while that column is still there; one whose column
 * is gone, or a mapping never set that detection gives a new column, takes
 * the detected value. A different spreadsheet or tab returns null, so the
 * caller takes the detected metadata as it is.
 */
function keptSheetMapping(
  saved: Partial<SheetFilters> | undefined,
  response: SheetColumnsResponse
): {
  columns: SheetColumn[];
  identity: SheetFilters['identity'];
  rowKey: string | null | undefined;
} | null {
  if (
    !saved ||
    saved.spreadsheetId !== response.spreadsheetId ||
    saved.sheetId !== response.sheetId ||
    !Array.isArray(saved.columns)
  )
    return null;
  const header = (column: SheetColumn) => column.header.trim().toLowerCase();
  const unique = (columns: SheetColumn[]) => {
    const count = new Map<string, number>();
    for (const column of columns)
      count.set(header(column), (count.get(header(column)) ?? 0) + 1);
    return new Map(
      columns
        .filter(
          (column) => header(column) !== '' && count.get(header(column)) === 1
        )
        .map((column) => [header(column), column])
    );
  };
  const savedByHeader = unique(saved.columns);
  const readHeaders = unique(response.columns);
  const matched = (column: SheetColumn) =>
    readHeaders.get(header(column)) === column
      ? savedByHeader.get(header(column))
      : undefined;
  const taken = new Set<string>();
  for (const column of response.columns) {
    const match = matched(column);
    if (match) taken.add(match.key);
  }
  // Read key to the key it is saved under.
  const keys = new Map<string, string>();
  const columns = response.columns.map((column) => {
    const match = matched(column);
    if (match) {
      keys.set(column.key, match.key);
      return { ...match, header: column.header };
    }
    const key = taken.has(column.key)
      ? columnKey(column.header, taken)
      : column.key;
    taken.add(key);
    keys.set(column.key, key);
    return { ...column, key };
  });
  const fresh = (key: string | null | undefined) =>
    key == null ? null : (keys.get(key) ?? null);
  // A saved column is still there when a read column kept its key.
  const there = new Set(
    response.columns
      .filter((column) => matched(column))
      .map((column) => fresh(column.key))
  );
  const added = new Set(
    response.columns
      .filter((column) => !matched(column))
      .map((column) => fresh(column.key))
  );
  const identity: SheetFilters['identity'] = {};
  const fields = new Set([
    ...Object.keys(saved.identity ?? {}),
    ...Object.keys(response.identity),
  ]) as Set<keyof SheetFilters['identity']>;
  for (const field of fields) {
    const before = saved.identity?.[field];
    const detected = fresh(response.identity[field]);
    if (before === undefined) {
      if (added.has(detected)) identity[field] = detected;
    } else {
      identity[field] =
        before === null || there.has(before) ? before : detected;
    }
  }
  let rowKey = saved.rowKey;
  if (rowKey != null && !there.has(rowKey)) {
    const detected =
      response.identity.externalId ?? response.identity.email ?? null;
    rowKey =
      detected && response.rowKeyCandidates.includes(detected)
        ? fresh(detected)
        : null;
  }
  return { columns, identity, rowKey };
}

/** Only the upstream picked trigger declares Sheet outputs on this route. */
export function upstreamSheetFields(
  graph: CustomGraph | null,
  nodeId: string
): import('@/components/smart-columns/advanced-filter-builder.component').FieldDefinition[] {
  if (!graph) return [];
  const seen = new Set([nodeId]);
  const pending = [nodeId];
  while (pending.length) {
    const current = pending.pop();
    for (const edge of graph.edges.filter((item) => item.target === current)) {
      if (seen.has(edge.source)) continue;
      seen.add(edge.source);
      const node = graph.nodes.find((item) => item.id === edge.source);
      if (!node) continue;
      pending.push(node.id);
      if (node.type !== 'trigger') continue;
      const config = node.config as Partial<TriggerConfig>;
      if (
        config.source !== 'google_sheet' ||
        config.kind !== 'sheet_rows_added' ||
        sheetTriggerIssue(config.sheet)
      )
        return [];
      return (config.sheet?.columns ?? []).map((column) => ({
        id: `trigger.${column.key}`,
        label: column.header || column.key,
        displayLabel: column.header || column.key,
        sourceLabel: config.sheet?.sheetName || 'Google Sheet',
        type: column.type,
        source: 'trigger',
      }));
    }
  }
  return [];
}
